import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, Form } from '@angular/forms';
import { MediaObserver, MediaChange } from '@angular/flex-layout';
import { Router, ActivatedRoute } from '@angular/router';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';

import { UtilityService } from 'src/app/services/utility.service';
import { UserService } from 'src/app/services/user.service';
import { ApplicationService } from '../../services/application.service';
import { RegistrationService } from '../../services/registration.service';
import { CompanyService } from '../../services/company.service';
import { VerifyService } from 'src/app/services/verify.service';

import { MessageListItem } from '../../models/application';
import { SecurityQuestionsListItem, UserInformation, VerifyUserRegistration } from '../../models/registration';
import { CompanyInformation } from '../../models/company';

@Component({
    selector: 'app-user-registration',
    templateUrl: './user-registration.component.html',
    styleUrls: ['./user-registration.component.scss']
})

export class UserRegistrationComponent implements OnInit {
    private _handleError: HandleError;
    private _logging = environment.logging;
    fieldValidations = environment.fieldValidations;
    fieldValidationsDateOfBirthMinValue = new Date(this.fieldValidations.userRegistration.dateOfBirthMinValue);
    fieldValidationsDateOfBirthMaxValue = new Date(
        this.fieldValidations.userRegistration.dateOfBirthMaxValue !== null
            ? this.fieldValidations.userRegistration.dateOfBirthMaxValue
            : new Date()
    );
    recaptchaSiteKey = environment.restAPI.recaptchaSiteKey;

    // isBrowserIEOrEdge = /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);
    isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

    showInstructionMessagesSpinner = false;
    instructionMessageList: MessageListItem[] = [];

    showSecurityQuestionsListSpinner = false;
    securityQuestionsList: SecurityQuestionsListItem[] = [];

    userRegistrationFormPart1: UntypedFormGroup;
    socialSecurityNumberFormControl = new UntypedFormControl();
    confirmSocialSecurityNumberFormControl = new UntypedFormControl();
    socialSecurityNumberEqualsFormControl = new UntypedFormControl();
    firstNameFormControl = new UntypedFormControl();
    lastNameFormControl = new UntypedFormControl();
    middleInitialFormControl = new UntypedFormControl();
    dateOfBirthFormControl = new UntypedFormControl();
    userNameFormControl = new UntypedFormControl();
    recaptchaFormControl = new UntypedFormControl();

    userRegistrationFormPart2: UntypedFormGroup;
    userPasswordFormControl = new UntypedFormControl();
    confirmUserPasswordFormControl = new UntypedFormControl();
    userPasswordEqualsFormControl = new UntypedFormControl();
    securityQuestionFormControl = new UntypedFormControl();
    securityQuestionAnswerFormControl = new UntypedFormControl();

    recaptchaValid = false;
    verifyUserRegistration: VerifyUserRegistration;
    registrationVerified = false;

    userAgreed = false;

    showVerifyRegistrationSpinner = false;
    showVerifyErrorParticipantId = false;
    showVerifyErrorRegisteredFlag = false;
    showVerifyErrorDuplicateUserNameFlag = false;
    showVerifyError = false;
    showSubmitSpinner = false;
    showLoginButton = false;
    showSubmitError = false;

    companyInformation: CompanyInformation;
    userInformation: UserInformation;

    constructor(
        private _route: ActivatedRoute,
        private _router: Router,
        private _formBuilder: UntypedFormBuilder,
        private _errorHandler: ErrorHandler,
        private _utilityService: UtilityService,
        private _userService: UserService,
        private _applicationService: ApplicationService,
        private _registrationService: RegistrationService,
        private _companyService: CompanyService,
        private _verifyService: VerifyService,

        public media: MediaObserver
    ) {
        this._handleError = _errorHandler.createHandleError('UserRegistrationComponent');
        this.getData();
        this.scrollToTop();
    }

    ngOnInit() { }

    getData(): void {
        const companyCode: string = this._companyService.getCompanyCode();

        this.userInformation = this._registrationService.getNewUserInformation();
        this.verifyUserRegistration = this._registrationService.getNewVerifyUserRegistration();

        this.companyInformation = this._companyService.getNewCompanyInformation();

        this._companyService.companyInformation.subscribe(
            response => (this.companyInformation = response),
            error => this._handleError('companyInformation.subscribe', error)
        );

        // this.getInstructionMessageList(companyCode);
        this.getSecurityQuestionsList();
    }

    getInstructionMessageList(companyCode: string): void {
        this.showInstructionMessagesSpinner = true;
        this._applicationService.getWelcomeMessageList(companyCode).subscribe(
            resultArray => {
                this.instructionMessageList = resultArray;
                if (this._logging.serviceCallTraceLog) {
                    console.log('UserRegistrationComponent.getInstructionMessageList :: result.length = ' + resultArray.length);
                }
                if (this._logging.serviceDataTraceLog) {
                    console.log('UserRegistrationComponent.getInstructionMessageList :: result = ' + JSON.stringify(resultArray));
                }
                this.showInstructionMessagesSpinner = false;
            },
            error => this._handleError('getInstructionMessageList', error)
        );
    }

    getSecurityQuestionsList(): void {
        this.showSecurityQuestionsListSpinner = true;
        this._registrationService.getSecurityQuestionsList().subscribe(
            resultArray => {
                this.securityQuestionsList = resultArray;
                if (this._logging.serviceCallTraceLog) {
                    console.log('UserRegistrationComponent.getSecurityQuestionsList :: result.length = ' + resultArray.length);
                }
                if (this._logging.serviceDataTraceLog) {
                    console.log('UserRegistrationComponent.getSecurityQuestionsList :: result = ' + JSON.stringify(resultArray));
                }
                this.showSecurityQuestionsListSpinner = false;
                this.createForm();
            },
            error => this._handleError('getSecurityQuestionsList', error)
        );
    }

    getMessageCssClass(item: MessageListItem): string {
        return this._applicationService.getMessageListItemDisplayClass(item);
    }

    createForm(): void {
        this.userRegistrationFormPart1 = this._formBuilder.group({});
        this.userRegistrationFormPart1.addControl('socialSecurityNumberFormControl', this.socialSecurityNumberFormControl);
        this.userRegistrationFormPart1.addControl('confirmSocialSecurityNumberFormControl', this.confirmSocialSecurityNumberFormControl);
        this.userRegistrationFormPart1.addControl('socialSecurityNumberEqualsFormControl', this.socialSecurityNumberEqualsFormControl);
        this.userRegistrationFormPart1.addControl('firstNameFormControl', this.firstNameFormControl);
        this.userRegistrationFormPart1.addControl('lastNameFormControl', this.lastNameFormControl);
        this.userRegistrationFormPart1.addControl('middleInitialFormControl', this.middleInitialFormControl);
        this.userRegistrationFormPart1.addControl('dateOfBirthFormControl', this.dateOfBirthFormControl);
        this.userRegistrationFormPart1.addControl('userNameFormControl', this.userNameFormControl);
        this.userRegistrationFormPart1.addControl('recaptchaFormControl', this.recaptchaFormControl);

        this.userRegistrationFormPart2 = this._formBuilder.group({});
        this.userRegistrationFormPart2.addControl('userPasswordFormControl', this.userPasswordFormControl);
        this.userRegistrationFormPart2.addControl('confirmUserPasswordFormControl', this.confirmUserPasswordFormControl);
        this.userRegistrationFormPart2.addControl('userPasswordEqualsFormControl', this.userPasswordEqualsFormControl);
        this.userRegistrationFormPart2.addControl('securityQuestionFormControl', this.securityQuestionFormControl);
        this.userRegistrationFormPart2.addControl('securityQuestionAnswerFormControl', this.securityQuestionAnswerFormControl);
        this.setFormValues(null);
        this.setFormValidations(false);

        this.userRegistrationFormPart1.get('socialSecurityNumberFormControl').valueChanges.subscribe(val => {
            this.socialSecurityNumberChanged(val);
        });

        this.userRegistrationFormPart1.get('confirmSocialSecurityNumberFormControl').valueChanges.subscribe(val => {
            this.socialSecurityNumberChanged(val);
        });

        this.userRegistrationFormPart2.get('userPasswordFormControl').valueChanges.subscribe(val => {
            this.userPasswordChanged(val);
        });

        this.userRegistrationFormPart2.get('confirmUserPasswordFormControl').valueChanges.subscribe(val => {
            this.userPasswordChanged(val);
        });
    }

    setFormValues(userInformation: UserInformation = null): void {
        if (userInformation === null) {
            userInformation = this._registrationService.getNewUserInformation();
        }

        this.userRegistrationFormPart1.setValue({
            socialSecurityNumberFormControl: userInformation.ssn,
            confirmSocialSecurityNumberFormControl: userInformation.ssn,
            socialSecurityNumberEqualsFormControl: true,
            firstNameFormControl: userInformation.firstName,
            lastNameFormControl: userInformation.lastName,
            middleInitialFormControl: userInformation.middleInitial,
            dateOfBirthFormControl: userInformation.birthDate,
            userNameFormControl: userInformation.userName,
            recaptchaFormControl: null
        });

        this.userRegistrationFormPart2.setValue({
            userPasswordFormControl: userInformation.password,
            confirmUserPasswordFormControl: userInformation.password,
            userPasswordEqualsFormControl: true,
            securityQuestionFormControl: userInformation.securityQuestionId !== null ? userInformation.securityQuestionId : '',
            securityQuestionAnswerFormControl: userInformation.securityQuestionAnswer
        });
    }

    setFormValidations(clearAllValidations: boolean) {
        if (clearAllValidations) {
            this.userRegistrationFormPart1.get('socialSecurityNumberFormControl').setValidators([]);
            this.userRegistrationFormPart1.get('confirmSocialSecurityNumberFormControl').setValidators([]);
            this.userRegistrationFormPart1.get('socialSecurityNumberEqualsFormControl').setValidators([]);
            this.userRegistrationFormPart1.get('firstNameFormControl').setValidators([]);
            this.userRegistrationFormPart1.get('lastNameFormControl').setValidators([]);
            this.userRegistrationFormPart1.get('middleInitialFormControl').setValidators([]);
            this.userRegistrationFormPart1.get('dateOfBirthFormControl').setValidators([]);
            this.userRegistrationFormPart1.get('userNameFormControl').setValidators([]);
            this.userRegistrationFormPart1.get('recaptchaFormControl').setValidators([]);

            this.userRegistrationFormPart2.get('userPasswordFormControl').setValidators([]);
            this.userRegistrationFormPart2.get('confirmUserPasswordFormControl').setValidators([]);
            this.userRegistrationFormPart2.get('userPasswordEqualsFormControl').setValidators([]);
            this.userRegistrationFormPart2.get('securityQuestionFormControl').setValidators([]);
            this.userRegistrationFormPart2.get('securityQuestionAnswerFormControl').setValidators([]);
        } else {
            this.userRegistrationFormPart1
                .get('socialSecurityNumberFormControl')
                .setValidators([
                    Validators.required,
                    Validators.minLength(this.fieldValidations.userRegistration.socialSecurityNumberMinLength),
                    Validators.maxLength(this.fieldValidations.userRegistration.socialSecurityNumberMaxLength)
                ]);
            this.userRegistrationFormPart1
                .get('confirmSocialSecurityNumberFormControl')
                .setValidators([
                    Validators.required,
                    Validators.minLength(this.fieldValidations.userRegistration.socialSecurityNumberMinLength),
                    Validators.maxLength(this.fieldValidations.userRegistration.socialSecurityNumberMaxLength)
                ]);
            this.userRegistrationFormPart1.get('socialSecurityNumberEqualsFormControl').setValidators([Validators.requiredTrue]);
            this.userRegistrationFormPart1.get('firstNameFormControl').setValidators([Validators.required]);
            this.userRegistrationFormPart1.get('lastNameFormControl').setValidators([Validators.required]);
            this.userRegistrationFormPart1.get('middleInitialFormControl').setValidators([]);
            this.userRegistrationFormPart1.get('dateOfBirthFormControl').setValidators([Validators.required]);
            this.userRegistrationFormPart1
                .get('userNameFormControl')
                .setValidators([
                    Validators.required,
                    Validators.minLength(this.fieldValidations.userRegistration.userNameMinLength),
                    Validators.maxLength(this.fieldValidations.userRegistration.userNameMaxLength),
                    Validators.pattern(this.fieldValidations.userRegistration.userNamePattern)
                ]);
            this.userRegistrationFormPart1.get('recaptchaFormControl').setValidators([Validators.required]);

            this.userRegistrationFormPart2
                .get('userPasswordFormControl')
                .setValidators([Validators.required, Validators.pattern(this.fieldValidations.userRegistration.passwordPattern)]);
            this.userRegistrationFormPart2
                .get('confirmUserPasswordFormControl')
                .setValidators([Validators.required, Validators.pattern(this.fieldValidations.userRegistration.passwordPattern)]);
            this.userRegistrationFormPart2.get('userPasswordEqualsFormControl').setValidators([Validators.requiredTrue]);
            this.userRegistrationFormPart2.get('securityQuestionFormControl').setValidators([Validators.required]);
            this.userRegistrationFormPart2.get('securityQuestionAnswerFormControl')
                .setValidators([Validators.required,
                                Validators.pattern(this.fieldValidations.userRegistration.securityAnswerPattern)]);
        }

        this.userRegistrationFormPart1.get('socialSecurityNumberFormControl').markAsPristine();
        this.userRegistrationFormPart1.get('confirmSocialSecurityNumberFormControl').markAsPristine();
        this.userRegistrationFormPart1.get('socialSecurityNumberEqualsFormControl').markAsPristine();
        this.userRegistrationFormPart1.get('firstNameFormControl').markAsPristine();
        this.userRegistrationFormPart1.get('lastNameFormControl').markAsPristine();
        this.userRegistrationFormPart1.get('middleInitialFormControl').markAsPristine();
        this.userRegistrationFormPart1.get('dateOfBirthFormControl').markAsPristine();
        this.userRegistrationFormPart1.get('userNameFormControl').markAsPristine();
        this.userRegistrationFormPart1.get('recaptchaFormControl').markAsPristine();

        this.userRegistrationFormPart2.get('userPasswordFormControl').markAsPristine();
        this.userRegistrationFormPart2.get('confirmUserPasswordFormControl').markAsPristine();
        this.userRegistrationFormPart2.get('userPasswordEqualsFormControl').markAsPristine();
        this.userRegistrationFormPart2.get('securityQuestionFormControl').markAsPristine();
        this.userRegistrationFormPart2.get('securityQuestionAnswerFormControl').markAsPristine();

        this.userRegistrationFormPart1.get('socialSecurityNumberFormControl').markAsUntouched();
        this.userRegistrationFormPart1.get('confirmSocialSecurityNumberFormControl').markAsUntouched();
        this.userRegistrationFormPart1.get('socialSecurityNumberEqualsFormControl').markAsUntouched();
        this.userRegistrationFormPart1.get('firstNameFormControl').markAsUntouched();
        this.userRegistrationFormPart1.get('lastNameFormControl').markAsUntouched();
        this.userRegistrationFormPart1.get('middleInitialFormControl').markAsUntouched();
        this.userRegistrationFormPart1.get('dateOfBirthFormControl').markAsUntouched();
        this.userRegistrationFormPart1.get('userNameFormControl').markAsUntouched();
        this.userRegistrationFormPart1.get('recaptchaFormControl').markAsUntouched();

        this.userRegistrationFormPart2.get('userPasswordFormControl').markAsUntouched();
        this.userRegistrationFormPart2.get('confirmUserPasswordFormControl').markAsUntouched();
        this.userRegistrationFormPart2.get('userPasswordEqualsFormControl').markAsUntouched();
        this.userRegistrationFormPart2.get('securityQuestionFormControl').markAsUntouched();
        this.userRegistrationFormPart2.get('securityQuestionAnswerFormControl').markAsUntouched();

        this.userRegistrationFormPart1.get('socialSecurityNumberFormControl').updateValueAndValidity();
        this.userRegistrationFormPart1.get('confirmSocialSecurityNumberFormControl').updateValueAndValidity();
        this.userRegistrationFormPart1.get('socialSecurityNumberEqualsFormControl').updateValueAndValidity();
        this.userRegistrationFormPart1.get('firstNameFormControl').updateValueAndValidity();
        this.userRegistrationFormPart1.get('lastNameFormControl').updateValueAndValidity();
        this.userRegistrationFormPart1.get('middleInitialFormControl').updateValueAndValidity();
        this.userRegistrationFormPart1.get('dateOfBirthFormControl').updateValueAndValidity();
        this.userRegistrationFormPart1.get('userNameFormControl').updateValueAndValidity();
        this.userRegistrationFormPart1.get('recaptchaFormControl').updateValueAndValidity();

        this.userRegistrationFormPart2.get('userPasswordFormControl').updateValueAndValidity();
        this.userRegistrationFormPart2.get('confirmUserPasswordFormControl').updateValueAndValidity();
        this.userRegistrationFormPart2.get('userPasswordEqualsFormControl').updateValueAndValidity();
        this.userRegistrationFormPart2.get('securityQuestionFormControl').updateValueAndValidity();
        this.userRegistrationFormPart2.get('securityQuestionAnswerFormControl').updateValueAndValidity();
    }

    socialSecurityNumberChanged(newConfirmSocialSecurityNumber: string) {
        this.userRegistrationFormPart1.controls['socialSecurityNumberEqualsFormControl'].setValue(this.areSocialSecurityNumbersEqual());
        this.userRegistrationFormPart1.controls['socialSecurityNumberEqualsFormControl'].markAsDirty();
        this.userRegistrationFormPart1.controls['socialSecurityNumberEqualsFormControl'].markAsTouched();
        this.userRegistrationFormPart1.get('socialSecurityNumberEqualsFormControl').updateValueAndValidity();
    }

    areSocialSecurityNumbersEqual(): boolean {
        return (
            this.userRegistrationFormPart1.controls['socialSecurityNumberFormControl'].value ===
            this.userRegistrationFormPart1.controls['confirmSocialSecurityNumberFormControl'].value
        );
    }

    userPasswordChanged(newConfirmUserPassword: string) {
        this.userRegistrationFormPart2.controls['userPasswordEqualsFormControl'].setValue(this.areUserPasswordsEqual());
        this.userRegistrationFormPart2.controls['userPasswordEqualsFormControl'].markAsDirty();
        this.userRegistrationFormPart2.controls['userPasswordEqualsFormControl'].markAsTouched();
        this.userRegistrationFormPart2.get('userPasswordEqualsFormControl').updateValueAndValidity();
    }

    areUserPasswordsEqual(): boolean {
        return (
            this.userRegistrationFormPart2.controls['userPasswordFormControl'].value ===
            this.userRegistrationFormPart2.controls['confirmUserPasswordFormControl'].value
        );
    }

    verifyEnabled(): boolean {
        return this.userRegistrationFormPart1.valid;
    }

    verifyRegistration() {
        if (this.userRegistrationFormPart1.valid) {
            this.showVerifyRegistrationSpinner = true;

            this.showVerifyErrorParticipantId = false;
            this.showVerifyErrorRegisteredFlag = false;
            this.showVerifyErrorDuplicateUserNameFlag = false;
            this.showVerifyError = false;

            this.userInformation = this._registrationService.getNewUserInformation();
            this.userInformation.ssn = this.userRegistrationFormPart1.get('socialSecurityNumberFormControl').value;
            this.userInformation.firstName = this.userRegistrationFormPart1.get('firstNameFormControl').value;
            this.userInformation.middleInitial = this.userRegistrationFormPart1.get('middleInitialFormControl').value;
            this.userInformation.lastName = this.userRegistrationFormPart1.get('lastNameFormControl').value;
            this.userInformation.birthDate = this.userRegistrationFormPart1.get('dateOfBirthFormControl').value;
            this.userInformation.userName = this.userRegistrationFormPart1.get('userNameFormControl').value;

            this._registrationService.verifyRegistration(this.userInformation).subscribe(
                result => {
                    if (this._logging.serviceCallTraceLog) {
                        console.log('UserRegistrationComponent.verifyRegistration :: result.participantId = ' + result.participantId);
                    }
                    if (this._logging.serviceDataTraceLog) {
                        console.log('UserRegistrationComponent.verifyRegistration :: result = ' + JSON.stringify(result));
                    }
                    this.showVerifyRegistrationSpinner = false;

                    if (result !== null) {
                        this.verifyUserRegistration = result;
                    } else {
                        this.verifyUserRegistration = this._registrationService.getNewVerifyUserRegistration();
                    }

                    this.updateVerifyRegistration(this.verifyUserRegistration);

                    this.showVerifyErrorParticipantId =
                        this.verifyUserRegistration.participantId === null || this.verifyUserRegistration.participantId === 0;
                    this.showVerifyErrorRegisteredFlag =
                        this.verifyUserRegistration.registeredFlag !== null && this.verifyUserRegistration.registeredFlag;
                    this.showVerifyErrorDuplicateUserNameFlag =
                        this.verifyUserRegistration.duplicateUserNameFlag !== null && this.verifyUserRegistration.duplicateUserNameFlag;

                    this.showVerifyError =
                        !this.registrationVerified &&
                        !this.showVerifyErrorParticipantId &&
                        !this.showVerifyErrorRegisteredFlag &&
                        !this.showVerifyErrorDuplicateUserNameFlag;
                },
                error => {
                    this._handleError('verifyRegistration', error, this.userInformation);
                    this.updateVerifyRegistration(null);
                    this.showVerifyError = true;
                }
            );
        } else {
            this.updateVerifyRegistration(null);
            this.showVerifyError = true;
        }
    }

    updateVerifyRegistration(verifyUserRegistration: VerifyUserRegistration): void {
        this.registrationVerified =
            verifyUserRegistration !== null &&
            verifyUserRegistration.participantId !== null &&
            verifyUserRegistration.participantId > 0 &&
            verifyUserRegistration.registeredFlag !== null &&
            !verifyUserRegistration.registeredFlag &&
            verifyUserRegistration.duplicateUserNameFlag !== null &&
            !verifyUserRegistration.duplicateUserNameFlag;
    }

    agreeEnabled(): boolean {
        return this.userRegistrationFormPart2.valid;
    }

    submitEnabled(): boolean {
        return this.userAgreed;
    }

    userAgreement(): void {
        this.userAgreed = true;
    }

    resetRegistration(): void {
        this.recaptchaValid = false;
        this.registrationVerified = false;

        this.showVerifyRegistrationSpinner = false;
        this.showVerifyErrorParticipantId = false;
        this.showVerifyErrorRegisteredFlag = false;
        this.showVerifyErrorDuplicateUserNameFlag = false;
        this.showVerifyError = false;
        this.showSubmitSpinner = false;
        this.showLoginButton = false;
        this.showSubmitError = false;
        this.userAgreed = false;

        this.userInformation = this._registrationService.getNewUserInformation();
        this.verifyUserRegistration = this._registrationService.getNewVerifyUserRegistration();

        this.setFormValues(null);
        this.setFormValidations(false);

        this.userRegistrationFormPart1.markAsPristine();
        this.userRegistrationFormPart1.markAsUntouched();
        this.userRegistrationFormPart2.markAsPristine();
        this.userRegistrationFormPart2.markAsUntouched();
    }

    submitUser(): void {
        if (this.userRegistrationFormPart2.valid) {
            this.showSubmitSpinner = true;

            this.userInformation.participantId = this.verifyUserRegistration.participantId;
            this.userInformation.password = this.userRegistrationFormPart2.get('userPasswordFormControl').value;
            this.userInformation.securityQuestionId = Number(this.userRegistrationFormPart2.get('securityQuestionFormControl').value);

            const selectedSecurityQuestionsList = this.securityQuestionsList.filter(
                x => x.securityQuestionId === Number(this.userRegistrationFormPart2.get('securityQuestionFormControl').value)
            );
            this.userInformation.securityQuestionText =
                selectedSecurityQuestionsList !== null && selectedSecurityQuestionsList.length > 0
                    ? selectedSecurityQuestionsList[0].securityQuestion
                    : null;
            this.userInformation.securityQuestionAnswer = this.userRegistrationFormPart2.get('securityQuestionAnswerFormControl').value;

            this._registrationService.addUser(this.userInformation).subscribe(
                result => {
                    if (this._logging.serviceCallTraceLog) {
                        console.log('UserRegistrationComponent.submitUser :: result = ' + result);
                    }
                    if (this._logging.serviceDataTraceLog) {
                        console.log('UserRegistrationComponent.submitUser :: result = ' + result);
                    }

                    this.showSubmitSpinner = false;

                    if (Number(result) === 0) {
                        this.showLoginButton = true;
                        this.showSubmitError = false;
                    } else {
                        this.showLoginButton = false;
                        this.showSubmitError = true;
                    }

                },
                error => {
                    this._handleError('submitUser', error, this.userInformation);
                    this.showLoginButton = false;
                    this.showSubmitError = true;
                }
            );
        } else {
            this.showSubmitSpinner = false;
            this.showLoginButton = false;
            this.showSubmitError = true;
        }
    }

    openLink(url: string, linkName: string): void {
        if (this._logging.navigationTraceLog) {
            console.log('PageHeaderComponent.openLink.' + linkName);
        }
        window.open(url, '_blank');
    }

    openLogin(): void {
        if (this._logging.navigationTraceLog) {
            console.log('UserRegistrationComponent.openLogin');
        }
        this._router.navigate(['login']);
    }

    openVerify(): void {
        if (this._logging.navigationTraceLog) {
            console.log('UserRegistrationComponent.openVerify');
        }
        this._router.navigate(['verify']);
    }

    getToolTip(element: string): string {
        let toolTip: string;
        switch (element.toUpperCase().trim()) {
            case 'SECURITYQUESTION': {
                toolTip =
                    // tslint:disable-next-line:max-line-length quotemark
                    "For your security and convenience, we are asking you to provide a security question (and corresponding answer), so that you may gain access to your account in the event you forget your User Name, Password, or both.  Security questions and answers are each limited to 40 characters in length.  We recommend choosing a question and answer that will be easy for you to remember, but hard for others to guess.  Some examples: \n\n Q: What is your favorite color? \n Q: What is your favorite restaurant? \n Q: What is your dog's name? \n\n The questions and answers are not restricted in any way, but we recommend you choose something that you will remember, and that won't change very often. (We will provide a tool to change them, if you so desire, however.)  We don't recommend choosing questions and answers that would be easy for others to obtain.  For example: \n\n Q: What is your address? \n Q: What is your Mother's maiden name? \n Q: What is your phone number?";
                break;
            }
            default: {
                toolTip = 'No ToolTip message has been defined for ' + element;
                break;
            }
        }
        return toolTip;
    }

    maskSocialSecurityNumber(socialSecurityNumber: string): string {
        return socialSecurityNumber.replace(/\d(?=\d{4})/g, '*');
    }

    scrollToTop() {
        const contentStart = document.getElementsByClassName('mat-sidenav-content');
        if (contentStart.length > 0) {
            contentStart[0].scrollTop = 0;
        }
    }
}
